import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
import { SsoElement } from '../components/sso-base.js';
import { loginWithPasskey, webauthnSupported } from '../auth/webauthn.js';
import { navigate } from '../router.js';
import '../components/sso-card.js';
import '../components/sso-button.js';
import '../components/sso-alert.js';

/**
 * `<passkey-login-page>` — WebAuthn get() assertion ceremony (SSO-MOCKUPS.md §5b).
 *
 * Confirms identity with an existing passkey via `loginWithPasskey`, which returns the server's
 * next-step instruction (possibly a terminal redirect). TOTP stays available as the fallback.
 */
@customElement('passkey-login-page')
@localized()
export class PasskeyLoginPage extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      .hero {
        display: grid;
        justify-items: center;
        gap: var(--sso-space);
        text-align: center;
      }
      .badge {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        background: color-mix(in srgb, var(--sso-color-brand) 14%, var(--sso-color-surface));
        display: grid;
        place-items: center;
        font-size: 2rem;
      }
      .links {
        display: grid;
        gap: 0.5rem;
        margin-top: var(--sso-space);
        text-align: center;
        font-size: 0.9rem;
      }
    `,
  ];

  @state() private error = '';
  @state() private busy = false;

  override firstUpdated(): void {
    if (!webauthnSupported()) {
      navigate('/2fa/totp');
      return;
    }
    this.focusFirstControl();
  }

  private async authenticate(): Promise<void> {
    this.busy = true;
    this.error = '';
    try {
      const r = await loginWithPasskey();
      if (r.next === 'complete') {
        this.complete();
        return;
      }
      this.error = msg('Could not verify your passkey. Please try again or use an authenticator code.');
    } catch (err) {
      if (err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
        this.error = msg('Passkey sign-in was cancelled. You can try again.');
      } else {
        this.error = msg('Could not verify your passkey. Please try again or use an authenticator code.');
      }
    } finally {
      this.busy = false;
    }
  }

  /** Second factor satisfied — follow the launcher-initiated return target if any, else launcher. */
  private complete(): void {
    const returnUri = this.resolveReturnUri();
    if (returnUri) {
      this.redirect(returnUri);
      return;
    }
    navigate('/');
  }

  override render() {
    return html`
      <sso-card>
        <div class="hero">
          <h1>${msg('Sign in with a passkey')}</h1>
          <div class="badge" aria-hidden="true">🔑</div>
          <p>${msg('Confirm your identity with your passkey.')}</p>
        </div>
        ${this.error ? html`<sso-alert kind="error">${this.error}</sso-alert>` : nothing}
        <sso-button variant="primary" ?busy=${this.busy} @click=${this.authenticate}>
          ${msg('Continue with passkey')}
        </sso-button>
        <div class="links">
          <a
            href="/2fa/totp"
            @click=${(e: Event) => { e.preventDefault(); navigate('/2fa/totp'); }}>
            ${msg('Prefer a code? Use an authenticator code')}
          </a>
          <a href="/logout">${msg('Cancel / Sign out')}</a>
        </div>
      </sso-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'passkey-login-page': PasskeyLoginPage;
  }
}
